/**
 * Iter 82.1 — Self-Service Account Settings.
 *
 * Drawer-style modal mounted in the top-right user menu. Lets a user:
 *   • update name + title
 *   • toggle notification preferences (8 whitelisted keys)
 *   • change password (current + new)
 *
 * Visual DNA inherits ShareModal / PlanExportModal / ObjectCardOverlay:
 *   - dark surface, orange accent, 44 px targets
 *   - sectioned headers with tracking-wider uppercase
 *   - sticky footer action bar
 *
 * NO admin UI. Customer self-service only.
 */
import React, { useEffect, useState } from "react";
import { X, User, Lock, Bell, CheckCircle2, Loader2, AlertCircle, Building2, Download } from "lucide-react";
import { api } from "@/lib/api";

const PREF_KEYS = [
  ["plan_uploaded",     "Plan uploaded"],
  ["plan_revised",      "Plan revised"],
  ["markup_assigned",   "Markup assigned to me"],
  ["conflict_raised",   "Conflict raised"],
  ["share_opened",      "Share opened"],
  ["export_completed",  "Export completed"],
  ["mentions",          "Mentions"],
  ["weekly_digest",     "Weekly digest"],
];

const Toggle = ({ on, onClick, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="min-h-[44px] flex items-center justify-between gap-3 w-full px-4 rounded-md bg-[var(--fop-surface-2)] border border-[var(--fop-border-2)] text-[13px] text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)] transition-colors"
  >
    <span>{label}</span>
    <span className={`h-5 w-9 rounded-full relative transition-colors ${on ? "bg-[var(--fop-primary)]" : "bg-[var(--fop-border-2)]"}`}>
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${on ? "left-4" : "left-0.5"}`} />
    </span>
  </button>
);

// Iter 83.2C-2 — One-click email pipeline validator. Sends a real email
// to the caller via the Resend provider abstraction; if the key is
// absent, the response says `skipped_no_key` (transparent, no surprise).
function TestEmailButton() {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const send = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(
        "/notifications/preferences/test-email", {});
      setResult({ ok: true, ...data });
    } catch (e) {
      setResult({ ok: false,
                   detail: e?.response?.data?.detail || "Send failed" });
    } finally {
      setBusy(false);
    }
  };
  const statusText = result && (
    result.ok
      ? (result.status === "delivered"
         ? `Sent to ${result.to}`
         : result.status === "skipped_no_key"
            ? "No RESEND_API_KEY — provider not provisioned yet"
            : `Status: ${result.status}`)
      : `Error: ${result.detail}`
  );
  return (
    <div className="mt-3 flex items-center gap-3" data-testid="account-test-email">
      <button
        type="button"
        onClick={send}
        disabled={busy}
        data-testid="account-test-email-btn"
        className="min-h-[36px] px-3 rounded-md border border-[var(--fop-primary)]/40 text-[12px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:bg-[var(--fop-primary)]/10 disabled:opacity-50 inline-flex items-center gap-2"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
        Send Test Email
      </button>
      {result && (
        <span className="text-[11px] text-[var(--fop-text-2)]"
              data-testid="account-test-email-status">
          {statusText}
        </span>
      )}
    </div>
  );
}

// Hard-Gate §22 self-service tenant data export (2026-09-07) — backend
// (`routers/tenant_export.py`) has been fully built and functional since
// 2026-09-06 (async job, expiring signed download, tenant isolation via
// JWT-derived tenant_id, full audit trail, 15-min cooldown) but had no
// frontend surface anywhere — a customer had no way to actually use it.
// This closes that gap: a single "Request Export" action + status list,
// visible only to owner/admin (matches `require_tenant_admin` server-side).
function CompanyDataSection({ role }) {
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const canExport = role === "owner" || role === "admin";

  const refresh = React.useCallback(() => {
    if (!canExport) return;
    api.get("/tenant/data-exports").then((r) => setJobs(r.data || [])).catch(() => {});
  }, [canExport]);

  useEffect(() => {
    refresh();
    const iv = setInterval(() => {
      if (jobs.some((j) => j.status === "queued" || j.status === "running")) refresh();
    }, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, jobs.length]);

  if (!canExport) return null;

  const requestExport = async () => {
    setBusy(true); setError(null);
    try {
      await api.post("/tenant/data-exports", {});
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        e?.response?.status === 403 && /MFA/i.test(detail || "")
          ? "Your account needs multi-factor authentication enrolled before requesting a company data export."
          : detail || "Export request failed."
      );
    } finally {
      setBusy(false);
    }
  };

  const download = async (job) => {
    setDownloading(job.id);
    try {
      const r = await api.get(`/tenant/data-exports/${job.id}/download`);
      if (r.data?.download_url) {
        window.open(r.data.download_url, "_blank");
      } else {
        // Filesystem backend (dev/Preview) streams the file directly.
        window.open(`${api.defaults.baseURL}/tenant/data-exports/${job.id}/download`, "_blank");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Download failed.");
    } finally {
      setDownloading(null);
    }
  };

  const latest = jobs[0];
  const inFlight = latest && (latest.status === "queued" || latest.status === "running");

  return (
    <section data-testid="company-data-export-section">
      <div className="text-[11px] uppercase tracking-wider text-[var(--fop-text-2)] mb-2 flex items-center gap-2">
        <Building2 size={11} /> Company Data
      </div>
      <p className="text-[12px] text-[var(--fop-text-2)] mb-2">
        Download every record your company owns — projects, sheets, users,
        objects, documents, photos, and the audit log — without contacting support.
      </p>
      <button
        type="button"
        onClick={requestExport}
        disabled={busy || inFlight}
        data-testid="company-export-request"
        className="min-h-[44px] w-full inline-flex items-center justify-center gap-2 rounded-md border border-[var(--fop-primary)]/40 text-[var(--fop-primary)] font-medium text-sm hover:bg-[var(--fop-primary)]/10 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy || inFlight ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
        {inFlight ? "Export in progress…" : "Request Export"}
      </button>

      {jobs.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="company-export-list">
          {jobs.slice(0, 5).map((j) => (
            <li
              key={j.id}
              data-testid={`company-export-row-${j.id}`}
              className="flex items-center justify-between gap-2 text-[12px] px-3 py-2 rounded-md bg-[var(--fop-surface-2)] border border-[var(--fop-border-2)]"
            >
              <span className="text-[var(--fop-text-2)]">
                {new Date(j.created_at).toLocaleString()} · {j.status}
                {j.size_bytes ? ` · ${(j.size_bytes / 1024).toFixed(0)} KB` : ""}
              </span>
              {j.status === "completed" ? (
                <button
                  type="button"
                  onClick={() => download(j)}
                  disabled={downloading === j.id}
                  data-testid={`company-export-download-${j.id}`}
                  className="text-[var(--fop-primary)] hover:underline whitespace-nowrap"
                >
                  {downloading === j.id ? "…" : "Download"}
                </button>
              ) : j.status === "failed" ? (
                <span className="text-[#ef4444] whitespace-nowrap">{j.error || "Failed"}</span>
              ) : (
                <Loader2 size={12} className="animate-spin text-[var(--fop-text-2)]" />
              )}
            </li>
          ))}
        </ul>
      )}
      {error && (
        <div className="mt-2 flex items-start gap-2 text-[12px] text-[#ef4444] bg-[#ef4444]/10 border border-[#ef4444]/40 rounded-md px-3 py-2" data-testid="company-export-error">
          <AlertCircle size={14} className="mt-0.5" /> {error}
        </div>
      )}
    </section>
  );
}

export default function AccountSettingsModal({ open, onClose }) {
  const [me, setMe] = useState(null);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [prefs, setPrefs] = useState({});
  const [pwCur, setPwCur] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMsg(null); setErr(null);
    api.get("/auth/me").then((r) => {
      const u = r.data?.user || r.data;
      setMe(u);
      setName(u.name || u.full_name || "");
      setTitle(u.title || "");
      setPrefs(u.notification_prefs || {});
    }).catch(() => setErr("Failed to load profile"));
  }, [open]);

  if (!open) return null;

  const saveProfile = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.patch("/users/me", { name, title, notification_prefs: prefs });
      setMsg("Profile saved.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async () => {
    if (pwNew.length < 12) { setErr("New password must be at least 12 characters."); return; }
    setPwBusy(true); setErr(null); setMsg(null);
    try {
      await api.post("/auth/password-change", {
        current_password: pwCur, new_password: pwNew,
      });
      setMsg("Password changed.");
      setPwCur(""); setPwNew("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Password change failed");
    } finally {
      setPwBusy(false);
    }
  };

  const togglePref = (k) =>
    setPrefs((p) => ({ ...p, [k]: !p[k] }));

  return (
    <div
      className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-6"
      data-testid="account-settings-modal"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-xl max-h-[92vh] overflow-y-auto rounded-t-xl sm:rounded-xl border border-[var(--fop-border-2)] bg-[var(--fop-surface)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--fop-border-2)]">
          <div className="flex items-center gap-2">
            <User size={18} className="text-[var(--fop-primary)]" />
            <h2 className="text-base font-semibold text-[var(--fop-text)]">Account Settings</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="account-settings-close"
            className="h-11 w-11 flex items-center justify-center rounded-md text-[var(--fop-text-2)] hover:bg-[var(--fop-surface-2)]"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Profile */}
          <section>
            <div className="text-[11px] uppercase tracking-wider text-[var(--fop-text-2)] mb-2">Profile</div>
            <div className="space-y-2">
              <label className="block">
                <span className="text-[11px] text-[var(--fop-text-2)]">Display Name</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="account-name"
                  className="w-full min-h-[44px] px-3 mt-1 rounded-md bg-[var(--fop-surface-2)] border border-[var(--fop-border-2)] text-sm text-[var(--fop-text)] focus:outline-none focus:border-[var(--fop-primary)]/60"
                />
              </label>
              <label className="block">
                <span className="text-[11px] text-[var(--fop-text-2)]">Title</span>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  data-testid="account-title"
                  className="w-full min-h-[44px] px-3 mt-1 rounded-md bg-[var(--fop-surface-2)] border border-[var(--fop-border-2)] text-sm text-[var(--fop-text)] focus:outline-none focus:border-[var(--fop-primary)]/60"
                />
              </label>
              <div className="text-[11px] text-[var(--fop-text-2)] pl-1">
                {me?.email}  ·  role: {me?.role}
              </div>
            </div>
          </section>

          {/* Notification prefs */}
          <section>
            <div className="text-[11px] uppercase tracking-wider text-[var(--fop-text-2)] mb-2 flex items-center gap-2">
              <Bell size={11} /> Notifications
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PREF_KEYS.map(([k, label]) => (
                <Toggle
                  key={k}
                  on={!!prefs[k]}
                  onClick={() => togglePref(k)}
                  label={label}
                  testid={`account-pref-${k}`}
                />
              ))}
            </div>
            <TestEmailButton />
          </section>

          {/* Password change */}
          <section>
            <div className="text-[11px] uppercase tracking-wider text-[var(--fop-text-2)] mb-2 flex items-center gap-2">
              <Lock size={11} /> Change Password
            </div>
            <div className="space-y-2">
              <input
                type="password"
                placeholder="Current password"
                value={pwCur}
                onChange={(e) => setPwCur(e.target.value)}
                data-testid="account-pw-current"
                className="w-full min-h-[44px] px-3 rounded-md bg-[var(--fop-surface-2)] border border-[var(--fop-border-2)] text-sm text-[var(--fop-text)] focus:outline-none focus:border-[var(--fop-primary)]/60"
              />
              <input
                type="password"
                placeholder="New password (≥12 chars)"
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                data-testid="account-pw-new"
                className="w-full min-h-[44px] px-3 rounded-md bg-[var(--fop-surface-2)] border border-[var(--fop-border-2)] text-sm text-[var(--fop-text)] focus:outline-none focus:border-[var(--fop-primary)]/60"
              />
              <button
                onClick={changePassword}
                disabled={pwBusy || !pwCur || pwNew.length < 12}
                data-testid="account-pw-submit"
                className="min-h-[44px] w-full inline-flex items-center justify-center gap-2 rounded-md bg-[var(--fop-cta-bg)] text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {pwBusy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Change Password
              </button>
            </div>
          </section>

          <CompanyDataSection role={me?.role} />

          {msg && (
            <div className="flex items-center gap-2 text-[12px] text-[#22c55e] bg-[#22c55e]/10 border border-[#22c55e]/40 rounded-md px-3 py-2" data-testid="account-success">
              <CheckCircle2 size={14} /> {msg}
            </div>
          )}
          {err && (
            <div className="flex items-start gap-2 text-[12px] text-[#ef4444] bg-[#ef4444]/10 border border-[#ef4444]/40 rounded-md px-3 py-2" data-testid="account-error">
              <AlertCircle size={14} className="mt-0.5" /> {err}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[var(--fop-border-2)] flex items-center justify-end gap-2 sticky bottom-0 bg-[var(--fop-surface)]">
          <button
            onClick={onClose}
            data-testid="account-cancel"
            className="min-h-[44px] px-4 rounded-md border border-[var(--fop-border-2)] text-[var(--fop-text-2)] hover:text-[var(--fop-text)] text-sm"
          >Close</button>
          <button
            onClick={saveProfile}
            disabled={busy}
            data-testid="account-save"
            className="min-h-[44px] px-5 rounded-md bg-[var(--fop-cta-bg)] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Save Profile
          </button>
        </div>
      </div>
    </div>
  );
}
